import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { messageKeyForError } from "../../i18n/errors";
import { ErrorState } from "../../components/ui/ErrorState";
import { Badge } from "../../components/ui/feedback";
import { RosterSkeleton } from "../roster/RosterSkeleton";
import { usePersons } from "../roster/queries";
import { changesQuery, configurationQuery, slotsQuery } from "./queries";
import { ConfigHeader } from "./ConfigHeader";
import { SlotEditor } from "./SlotEditor";

export interface SlotEditorPageProps {
  workspaceId: string;
  configurationId: string;
}

/** Lineup page for one configuration: header, locked band, changed list, then the lineup. */
export function SlotEditorPage({ workspaceId, configurationId }: SlotEditorPageProps) {
  const { t } = useI18n();
  const configuration = useQuery(configurationQuery(workspaceId, configurationId));
  const slots = useQuery(slotsQuery(workspaceId, configurationId));
  const changes = useQuery(changesQuery(workspaceId, configurationId));
  const persons = usePersons(workspaceId, false);
  const [resetCount, setResetCount] = useState(0);

  const failed = configuration.error ?? slots.error ?? persons.error ?? changes.error;
  if (failed) {
    return (
      <ErrorState
        action={
          <Link
            to="/app/$workspaceId/configurations"
            params={{ workspaceId }}
            className="inline-flex min-h-11 items-center font-semibold text-blue hover:underline"
          >
            {t("editor.back")}
          </Link>
        }
      >
        {t(messageKeyForError(failed))}
      </ErrorState>
    );
  }
  if (!configuration.data || !slots.data || !persons.data || !changes.data) {
    return <RosterSkeleton />;
  }

  const personNames = new Map(persons.data.map((person) => [person.id, person.name]));
  const changed = changes.data.changedPersonIds
    .map((id) => personNames.get(id))
    .filter((name): name is string => name !== undefined);
  const locked = configuration.data.locked;
  const changedIds = new Set(changes.data.changedPersonIds);
  const lineupIds = slots.data.map((slot) => slot.personId);
  const allChanged = lineupIds.length > 0 && lineupIds.every((id) => changedIds.has(id));

  return (
    <div className="space-y-6">
      <ConfigHeader workspaceId={workspaceId} configuration={configuration.data} tab="slots" />
      {locked ? (
        <div
          role="status"
          className="-mx-4 bg-red px-4 py-3 font-display text-lg leading-none font-extrabold tracking-[0.01em] text-white uppercase md:mx-0 md:rounded-control"
        >
          {t("editor.lockedBand")}
        </div>
      ) : null}
      {changed.length > 0 && allChanged ? (
        <details className="group rounded-panel bg-ice-2 p-4">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 font-semibold text-ink focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-blue">
            {t("editor.changedSummary", { count: changed.length })}
            <ChevronDown
              aria-hidden="true"
              className="size-[18px] shrink-0 text-blue transition-transform group-open:rotate-180"
              strokeWidth={1.75}
            />
          </summary>
          <ul className="mt-2 divide-y divide-hairline">
            {changed.map((name) => (
              <li key={name} className="py-2 font-semibold text-ink">
                {name}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {changed.length > 0 && !allChanged ? (
        <section aria-labelledby="changed-title" className="space-y-2 rounded-panel bg-ice-2 p-4">
          <h2
            id="changed-title"
            className="font-display text-lg leading-none font-extrabold uppercase"
          >
            {t("editor.changedList")}
          </h2>
          <ul className="divide-y divide-hairline">
            {changed.map((name) => (
              <li key={name} className="flex items-center justify-between gap-3 py-2">
                <span className="truncate font-semibold text-ink">{name}</span>
                <Badge tone="accent">{t("roster.changed")}</Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <SlotEditor
        key={`${configurationId}:${resetCount}`}
        workspaceId={workspaceId}
        configurationId={configurationId}
        locked={locked}
        serverSlots={slots.data}
        persons={persons.data}
        onDiscard={() => setResetCount((count) => count + 1)}
      />
    </div>
  );
}
